import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Info } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const TOOLTIP_TEXT =
  "This figure shows the USD total in your home currency at today\u2019s rate, for reference only. It is not used for any tax figure.";

/**
 * Shared hook: fetch the home-currency estimate for a given USD amount.
 * Returns { data: { estimate: { currency, amount, rate, rate_date } | null } }.
 * When home_currency is USD, the estimate is null (nothing to show).
 */
export function useHomeEstimate(usdAmount) {
  return useQuery({
    queryKey: ["home-estimate", usdAmount],
    queryFn: async () => (await api.get(`/fx/home-estimate?usd_amount=${usdAmount}`)).data,
    enabled: usdAmount != null && usdAmount > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Displays a home-currency estimate line under a USD total.
 * Reusable across Gifts, Contributions, and future surfaces.
 *
 * @param {{ currency: string, amount: number, rate: number, rate_date: string } | null} estimate
 */
export function HomeCurrencyEstimate({ estimate }) {
  if (!estimate) return null;
  return (
    <div className="flex items-center gap-1 text-xs italic text-[#6b7280]" data-testid="home-currency-estimate">
      <span className="tabular-nums">
        ≈ {money(estimate.amount, estimate.currency)} at today&apos;s rate ({fmtDate(estimate.rate_date)})
      </span>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info size={13} weight="bold" className="shrink-0 cursor-help text-[#9aa1a9]" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            {TOOLTIP_TEXT}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
